import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  RootDiv,
  InsuranceItem,
  TitleTypography,
  InformationTypography,
  TextContainer,
  SwitchButton,
  AddInsuranceContainer,
  IconContainer,
  LoaderContainer,
  InsuranceSkeletonContainer,
} from '../styles/InsuranceOptions.styles';
import AddIcon from '@mui/icons-material/Add';
import { useModalRouter } from '@hooks/useModalRouter';
import { Console, getRoute } from '@utils';
import { CircularProgress, Divider, useEventCallback } from '@mui/material';
import { useAuth } from '@hooks/useAuth';
import { usePaymentMethodActions } from '../zustand/paymentMethodStore';
import { AddInsuranceTypography } from '../styles/InsuranceOptions.styles';
import { INSURANCE_EMAILS_ALLOWED } from '@config/environment';
import { SkeletonContainer } from '@screens/insurance/my-insurances/styles';
import { StyledSkeleton } from '@components/Reminders/reminderCardSkeleton.styles';
import { usePermissions } from '@hooks/usePermissions';
import { useMekiState } from '@hooks/useMekiState';
import { UserInsurance } from '@interfaces/userInsurance.interface';
import { ApolloError } from '@apollo/client';
import {
  ArrowIconContainer,
  CardIconContainer,
  ErrorTypography,
  NewTagContainer,
  OptionDescriptionText,
  OptionDiv,
  OptionLeftContainer,
  OptionRightContainer,
  OptionTitleText,
  PaymentOptionSubSection,
  SkeletonOptionDescriptionText,
  SkeletonOptionTitleText,
  TextDiv,
} from './PaymentsOptions.styles';
import { PaymentMethodMainCard } from '../styles';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import { SubtitleTypography } from '@screens/checkout/styles';
import { CardIcon } from '@screens/checkout/components/CardIcon';
import { InteractionEventTypes } from '@validators/Events/Interactions';
import { useEventTracking } from '@hooks/useEventTracking';
interface InsuranceOptionsProps {
  loadInsuranceQuote: (rut: string) => Promise<void>;
  loadingInsurances: boolean;
  mekiCreditsChecked?: boolean;
  userInsurances: UserInsurance[];
  loadingCircle?: boolean;
  quoteError?: ApolloError;
}

export function InsuranceOptions(props: InsuranceOptionsProps) {
  const { loadingInsurances, loadInsuranceQuote, userInsurances } = props;
  const { track } = useEventTracking();
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const { insuranceId, setInsuranceId, setInsuranceRut } = useMekiState();
  const { appUser: user } = useAuth();
  const { userCan } = usePermissions();

  const modalRouter = useModalRouter();
  const isLocalVendor = userCan('LOCAL_VENDOR');

  const isPermittedUser = !INSURANCE_EMAILS_ALLOWED
    ? true
    : INSURANCE_EMAILS_ALLOWED.includes(user?.email ?? '');

  const handleInsuranceClick = useCallback(
    async (insuranceId) => {
      try {
        setShowLoader(true);
        const selected = userInsurances.find(
          (insurance) => insurance.id === insuranceId,
        );
        if (selected) {
          setInsuranceId(insuranceId);
          setInsuranceRut(selected.rut);
          await loadInsuranceQuote(selected.rut);
        }
      } catch (e) {
        track(InteractionEventTypes.UnselectInsurance);
        setInsuranceId(null);
      } finally {
        setShowLoader(false);
      }
    },
    [userInsurances, loadInsuranceQuote, setInsuranceId, setInsuranceRut],
  );

  const insuranceRedirect = () => {
    if (isLocalVendor) {
      return void modalRouter.push(
        getRoute('insuranceIntroduction', {
          from: 'checkout',
          redirect: 'checkoutPaymentMethodLocal',
        }),
      );
    }
    track(InteractionEventTypes.GoToInsurance);
    return void modalRouter.push(
      getRoute('insuranceIntroduction', {
        from: 'checkout',
        redirect: 'checkoutPaymentMethod',
      }),
    );
  };

  if (!isPermittedUser) {
    return null;
  }
  const insurances = userInsurances.map((insurance) => ({
    id: insurance.id,
    rut: insurance.rut,
    name: insurance.name,
  }));
  if (loadingInsurances) {
    return (
      <PaymentOptionSubSection>
        <SubtitleTypography>Seguro complementario</SubtitleTypography>
        <PaymentMethodMainCard>
          <OptionDiv key={0}>
            <OptionLeftContainer>
              <CardIconContainer />
              <TextDiv>
                <SkeletonOptionTitleText />
                <SkeletonOptionDescriptionText />
              </TextDiv>
            </OptionLeftContainer>
            <ArrowIconContainer>
              <ArrowForwardIosRoundedIcon color="primary" />
            </ArrowIconContainer>
          </OptionDiv>
        </PaymentMethodMainCard>
      </PaymentOptionSubSection>
    );
  }
  if (insurances.length === 0) {
    return (
      <PaymentMethodMainCard>
        <OptionDiv onClick={insuranceRedirect}>
          <OptionLeftContainer>
            <CardIcon creditCardType="Insurance" />
            <TextDiv>
              <OptionTitleText>Paga con tu seguro</OptionTitleText>
              <OptionDescriptionText light>
                automáticamente
              </OptionDescriptionText>
            </TextDiv>
          </OptionLeftContainer>
          <OptionRightContainer>
            <NewTagContainer>Nuevo</NewTagContainer>
            <ArrowIconContainer>
              <ArrowForwardIosRoundedIcon color="primary" />
            </ArrowIconContainer>
          </OptionRightContainer>
        </OptionDiv>
      </PaymentMethodMainCard>
    );
  }
  return (
    <PaymentOptionSubSection>
      <SubtitleTypography>Seguro complementario</SubtitleTypography>
      <PaymentMethodMainCard>
        {insurances.map((insurance) => {
          return (
            <Fragment key={insurance.id}>
              <OptionDiv onClick={() => handleInsuranceClick(insurance.id)}>
                <OptionLeftContainer>
                  <CardIcon creditCardType="Insurance" />
                  <TextDiv>
                    <OptionTitleText>Seguro {insurance.name}</OptionTitleText>
                    <OptionDescriptionText light>
                      Rut {insurance.rut}
                    </OptionDescriptionText>
                  </TextDiv>
                </OptionLeftContainer>

                <ArrowIconContainer>
                  {showLoader && insuranceId === insurance.id ? (
                    <LoaderContainer>
                      <CircularProgress
                        size={25}
                        thickness={4}
                        color="primary"
                      />
                    </LoaderContainer>
                  ) : (
                    <ArrowForwardIosRoundedIcon color="primary" />
                  )}
                </ArrowIconContainer>
              </OptionDiv>
              <Divider />
            </Fragment>
          );
        })}
        <OptionDiv onClick={insuranceRedirect}>
          <OptionLeftContainer>
            <CardIcon creditCardType="NewInsurance" />
            <TextDiv>
              <OptionTitleText>Agregar seguro</OptionTitleText>
              <OptionDescriptionText light>
                Vida Security, Sura, Bci, Vida Cámara, BiceVida, Zurich, Help
              </OptionDescriptionText>
            </TextDiv>
          </OptionLeftContainer>
          <ArrowIconContainer>
            <ArrowForwardIosRoundedIcon color="primary" />
          </ArrowIconContainer>
        </OptionDiv>
      </PaymentMethodMainCard>
    </PaymentOptionSubSection>
  );
}
